// 🔐 Hook para gestión de permisos basados en perfil
import React from 'react';
import { useAuth } from './useAuth';
import type { Perfil } from '../api/auth.api';

/**
 * Hook para verificar permisos del usuario actual
 */
export function usePermissions() {
  const { user } = useAuth();

  /**
   * Verificar si el usuario tiene un permiso específico
   * @param resource - Recurso a verificar (ej: 'usuarios', 'empresas')
   * @param action - Acción a verificar (ej: 'ver', 'crear', 'editar', 'eliminar')
   * @returns true si el usuario tiene el permiso, false en caso contrario
   *
   * @example
   * const { can } = usePermissions();
   * if (can('usuarios', 'crear')) {
   *   // Mostrar botón de crear usuario
   * }
   */
  const can = (resource: string, action: string): boolean => {
    if (!user?.perfil?.permisos) return false;

    const resourcePerms = user.perfil.permisos[resource];
    if (!resourcePerms) return false;

    return resourcePerms[action] === true;
  };

  /**
   * Verificar si el usuario tiene acceso a múltiples permisos
   * @param checks - Array de checks en formato [resource, action]
   * @returns true si el usuario tiene todos los permisos, false si falta alguno
   *
   * @example
   * const { canAll } = usePermissions();
   * if (canAll([['usuarios', 'ver'], ['usuarios', 'editar']])) {
   *   // Mostrar formulario de edición
   * }
   */
  const canAll = (checks: Array<[string, string]>): boolean => {
    return checks.every(([resource, action]) => can(resource, action));
  };

  /**
   * Verificar si el usuario tiene al menos uno de los permisos
   * @param checks - Array de checks en formato [resource, action]
   * @returns true si el usuario tiene al menos uno de los permisos
   *
   * @example
   * const { canAny } = usePermissions();
   * if (canAny([['usuarios', 'crear'], ['usuarios', 'editar']])) {
   *   // Mostrar sección de gestión
   * }
   */
  const canAny = (checks: Array<[string, string]>): boolean => {
    return checks.some(([resource, action]) => can(resource, action));
  };

  /**
   * Verificar si el usuario tiene un nivel de acceso mínimo
   * @param minLevel - Nivel mínimo requerido (1-5)
   * @returns true si el usuario tiene el nivel requerido o superior
   *
   * @example
   * const { hasAccessLevel } = usePermissions();
   * if (hasAccessLevel(4)) {
   *   // Permitir acceso a funciones ejecutivas
   * }
   */
  const hasAccessLevel = (minLevel: number): boolean => {
    if (!user?.perfil?.nivel_acceso) return false;
    return user.perfil.nivel_acceso >= minLevel;
  };

  /**
   * Verificar si el usuario es administrador (nivel 5)
   * @returns true si el usuario es administrador
   */
  const isAdmin = (): boolean => {
    return hasAccessLevel(5);
  };

  /**
   * Verificar si el usuario tiene un perfil específico
   * @param perfilNombre - Nombre del perfil a verificar
   * @returns true si el usuario tiene ese perfil
   *
   * @example
   * const { hasRole } = usePermissions();
   * if (hasRole('doctor')) {
   *   // Mostrar interfaz médica
   * }
   */
  const hasRole = (perfilNombre: string): boolean => {
    return user?.perfil?.nombre.toLowerCase() === perfilNombre.toLowerCase();
  };

  /**
   * Obtener todos los permisos del usuario como objeto
   * @returns Objeto con todos los permisos del usuario
   */
  const getPermissions = (): Perfil['permisos'] | null => {
    return user?.perfil?.permisos || null;
  };

  /**
   * Obtener el nivel de acceso del usuario
   * @returns Nivel de acceso (1-5) o null si no está autenticado
   */
  const getAccessLevel = (): number | null => {
    return user?.perfil?.nivel_acceso || null;
  };

  /**
   * Obtener el nombre del perfil del usuario
   * @returns Nombre del perfil o null si no está autenticado
   */
  const getRoleName = (): string | null => {
    return user?.perfil?.nombre || null;
  };

  return {
    can,
    canAll,
    canAny,
    hasAccessLevel,
    isAdmin,
    hasRole,
    getPermissions,
    getAccessLevel,
    getRoleName,
    // Exponer el perfil completo para casos avanzados
    perfil: user?.perfil || null,
  };
}

/**
 * Componente de orden superior para proteger componentes por permisos
 * @param Component - Componente a proteger
 * @param resource - Recurso requerido
 * @param action - Acción requerida
 * @param fallback - Componente alternativo si no tiene permisos
 *
 * @example
 * const ProtectedButton = withPermission(
 *   () => <button>Crear Usuario</button>,
 *   'usuarios',
 *   'crear',
 *   () => <p>No tienes permisos</p>
 * );
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  resource: string,
  action: string,
  fallback?: React.ComponentType<P>
) {
  return function ProtectedComponent(props: P) {
    const { can } = usePermissions();

    if (!can(resource, action)) {
      return fallback ? React.createElement(fallback, props) : null;
    }

    return React.createElement(Component, props);
  };
}
